import { useCallback } from "react";

/**
 * Lightweight keyboard navigation for table rows. Apply the returned `getRowProps(index)` to
 * each row element. Handles ArrowDown/Up, Home/End, Enter.
 *
 * Usage:
 *   const { getRowProps } = useKeyboardRowNav(rows.length, (i) => onRowClick(rows[i]));
 *   <tr {...getRowProps(i)}>...</tr>
 */
export function useKeyboardRowNav(rowCount: number, onActivate: (index: number) => void) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>, index: number) => {
      const moveTo = (next: number) => {
        e.preventDefault();
        const tbody = e.currentTarget.parentElement;
        if (!tbody) return;
        const target = tbody.children[next] as HTMLTableRowElement | undefined;
        target?.focus();
      };
      switch (e.key) {
        case "ArrowDown":
          if (index < rowCount - 1) moveTo(index + 1);
          break;
        case "ArrowUp":
          if (index > 0) moveTo(index - 1);
          break;
        case "Home":
          if (rowCount > 0) moveTo(0);
          break;
        case "End":
          if (rowCount > 0) moveTo(rowCount - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onActivate(index);
          break;
      }
    },
    [rowCount, onActivate],
  );

  const getRowProps = (index: number) => ({
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => handleKeyDown(e, index),
  });

  return { getRowProps };
}
